import type { LegalEntityRef, ShopRef, TenantRef } from '@serievo/contracts';

/**
 * Ngữ cảnh làm việc lưu trên URL: ?company=&entity=&shop=&period=
 * Công ty (tenant) → Pháp nhân → Shop → Kỳ. Đổi cấp trên thì bỏ chọn cấp dưới.
 * Logic thuần, không phụ thuộc React, để test được.
 */
export interface ContextSelection {
  company?: string;
  entity?: string;
  shop?: string;
  period?: string;
}

const KEYS = ['company', 'entity', 'shop', 'period'] as const;
const ID_RE = /^[1-9]\d{0,18}$/;
const PERIOD_RE = /^\d{4}-((0[1-9]|1[0-2])|Q[1-4])$/;

export function readSelection(params: URLSearchParams): ContextSelection {
  const s: ContextSelection = {};
  for (const k of KEYS) {
    const v = params.get(k);
    if (!v) continue;
    if (k === 'period' ? PERIOD_RE.test(v) : ID_RE.test(v)) s[k] = v;
  }
  return s;
}

export function writeSelection(s: ContextSelection): URLSearchParams {
  const p = new URLSearchParams();
  for (const k of KEYS) if (s[k]) p.set(k, s[k]!);
  return p;
}

/** Áp thay đổi của người dùng: chọn lại công ty thì bỏ pháp nhân + shop; chọn lại pháp nhân thì bỏ shop. */
export function applyChange(cur: ContextSelection, patch: ContextSelection): ContextSelection {
  const next: ContextSelection = { ...cur, ...patch };
  if ('company' in patch && patch.company !== cur.company) {
    delete next.entity;
    delete next.shop;
  }
  if ('entity' in patch && patch.entity !== cur.entity) delete next.shop;
  for (const k of KEYS) if (next[k] === undefined) delete next[k];
  return next;
}

/** Kỳ tháng hiện tại theo giờ Việt Nam (UTC+7, không có giờ mùa hè). */
export function currentVnMonth(now: Date = new Date()): string {
  const v = new Date(now.getTime() + 7 * 3600_000);
  return `${v.getUTCFullYear()}-${String(v.getUTCMonth() + 1).padStart(2, '0')}`;
}

export interface ResolvedContext {
  selection: ContextSelection;
  tenant?: TenantRef;
  entity?: LegalEntityRef;
  shop?: ShopRef;
  /** true nếu URL thiếu hoặc trỏ tới mục không còn quyền, cần thay bằng selection. */
  changed: boolean;
}

/** Đối chiếu lựa chọn trên URL với dữ liệu được phép; thiếu thì chọn mục đầu tiên, shop để trống = tất cả. */
export function resolveContext(
  tenants: readonly TenantRef[],
  s: ContextSelection,
  now: Date = new Date(),
): ResolvedContext {
  const tenant = tenants.find((t) => t.id === s.company) ?? tenants[0];
  const entity = tenant?.legalEntities.find((e) => e.id === s.entity) ?? tenant?.legalEntities[0];
  const shop = s.shop ? entity?.shops.find((x) => x.id === s.shop) : undefined;
  const selection: ContextSelection = {
    ...(tenant ? { company: tenant.id } : {}),
    ...(entity ? { entity: entity.id } : {}),
    ...(shop ? { shop: shop.id } : {}),
    period: s.period ?? tenant?.latestPeriod ?? currentVnMonth(now),
  };
  const changed = KEYS.some((k) => selection[k] !== s[k]);
  return { selection, ...(tenant ? { tenant } : {}), ...(entity ? { entity } : {}), ...(shop ? { shop } : {}), changed };
}
